import { isEqual } from 'lodash-es';
import { makeAutoObservable } from 'mobx';
import i18n from '@/i18n';
import { FIELD_ALL } from '@/proto/field';
import { TimeType } from '@/proto/metric';

type SubItem<T> = {
  data: T,
  isCheck: boolean,
};

type ItemData<T> = {
  key: string,
  dataList: SubItem<T>[],
};

export interface Data {
  id: string,
}

export interface CompareItem extends Data {
  fields: Record<string, any>,
  token?: string,
}

type IsSameFunc<T> = (data1: T, data2: T) => boolean;
type CanAddFunc<T> = (data: T, items: SubItem<T>[]) => void;

const defaultIsSame = (data1: any, data2: any) => isEqual(data1, data2);
const defaultCanAdd = () => true;


const getTimeWindowNames = (timeWindow: number) => {
  switch (timeWindow) {
    case TimeType.HOUR:
      return i18n.t('按小时聚合');
    case TimeType.MINUTE5:
      return i18n.t('按5分钟聚合');
    case TimeType.DAY:
      return i18n.t('按天聚合');
    default:
      return '';
  }
};

export const oneDayLimit = (data: CompareItem, items: SubItem<CompareItem>[]) => {
  const { time_window: itemTimeWindow, time_window_type: itemTimeWindowType } = items[0]?.data?.fields ?? {};
  const { time_window: timeWindow, time_window_type: timeWindowType } = data?.fields ?? {};
  if (items.length && (itemTimeWindow || itemTimeWindowType) !== (timeWindow || timeWindowType)) {
    throw new Error(i18n.t('对比列表当前包含{{timeWindow}}的数据，如果想对比其他聚合粒度的数据，请先清空对比列表。', { timeWindow: getTimeWindowNames(itemTimeWindow) }));
  }
};

export const oneDayLimitByStandardFields = (data: CompareItem, items: SubItem<CompareItem>[]) => {
  const itemTimeWindow = items[0]?.data?.fields?.time_window_type;
  if (items.length && itemTimeWindow !== data?.fields?.time_window_type) {
    throw new Error(i18n.t('对比列表当前包含{{timeWindow}}的数据，如果想对比其他聚合粒度的数据，请先清空对比列表。', { timeWindow: getTimeWindowNames(itemTimeWindow) }));
  }
};

export const isSameCompareItem = (data1: CompareItem, data2: CompareItem) => {
  const deleteEmptyFields = (fields: Record<string, any>) => {
    const obj: Record<string, any> = {};
    Object.keys(fields).forEach((key) => {
      if (fields[key] !== FIELD_ALL) {
        obj[key] = fields[key];
      }
    });
    return obj;
  };
  return isEqual(deleteEmptyFields(data1.fields), deleteEmptyFields(data2.fields));
};

export class CompareStore<T> {
  private data: Record<string, ItemData<T>>;
  private maxLength: number;
  private isSame: IsSameFunc<T>;
  private canAdd: CanAddFunc<T>;

  constructor(config: { isSame?: IsSameFunc<T>, canAdd?: CanAddFunc<T> }) {
    this.data = {};
    this.isSame = config.isSame ?? defaultIsSame;
    this.canAdd = config.canAdd ?? defaultCanAdd;
    this.maxLength = 10;
    makeAutoObservable(this);
  }

  public addItem(key: string, data: T, isChecked?: boolean): boolean | never {
    if (!this.withinLimit(key)) {
      throw new Error(i18n.t('最多添加10个至对比列表，已到上限'));
    }
    if (this.existItem(key, data)) {
      if (isChecked) {
        this.checked(key, data);
      }
      throw new Error(i18n.t('已添加至对比列表，不可重复添加'));
    }

    const items = this.getItems(key);
    this.canAdd(data, items);
    items.push({
      data,
      isCheck: false,
    });
    this.setItems(key, items);
    if (isChecked) {
      this.checked(key, data);
    }
    return true;
  }

  public checked(key: string, data: T) {
    const item = this.getItems(key).find(item => this.isSame(item.data, data));
    if (item) {
      item.isCheck = true;
    }
  }

  public getItems(key: string) {
    return this.data[key]?.dataList ?? [];
  }

  public getRawItems(key: string) {
    return this.getItems(key).map(item => item.data);
  }

  public hasCheckedItems(key: string) {
    return this.getCheckedItems(key).length > 0;
  }

  public getCheckedItems(key: string) {
    return this.getItems(key).filter(item => item.isCheck)
      .map(item => item.data);
  }

  public withinLimit(key: string) {
    return this.getItems(key).length < this.maxLength;
  }

  public removeCheckedItems(key: string) {
    this.setItems(key, this.getItems(key).filter(item => !item.isCheck));
  }

  public removeSelectedItems(key: string, selectedRowKeys: React.Key[]) {
    this.setItems(key, this.getItems(key).filter((item) => {
      const data = item.data as unknown as Data;
      return !selectedRowKeys.includes(data.id);
    }));
  }

  public removeSingleItem(key: string, index: number) {
    const items = this.getItems(key);
    items.splice(index, 1);
    this.setItems(key, items);
  }

  public removeAllRawItems() {
    this.data = {};
  }

  private setItems(key: string, items: SubItem<T>[]) {
    if (this.data[key]) {
      this.data[key].dataList = items;
    } else {
      this.data[key] = {
        key,
        dataList: items,
      };
    }
  }

  private existItem(key: string, data: T) {
    return this.getItems(key).find(item => this.isSame(data, item.data));
  }
}
